"use client";

import {
  CSSProperties,
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { cn } from "@/lib/utils";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import {
  useHoverSelect,
  useLegendHoverSelect,
  useSelectionGroup,
} from "@/lib/hooks/use-hover-select";
import { scrollIntoViewX } from "@/lib/scroll-into-view-x";
import type { ModuleParallelCategoryTrees } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { importsTokenScope } from "./imports-token-scope";
import { legendIndexOfCategory } from "./shared/category-resolve";
import { useHierarchy, hierarchyDemoData } from "./shared/hierarchies";
import { useSvgContentHeight } from "./shared/use-svg-content-height";
import {
  useTextMetrics,
  type TextMetricsItem,
} from "./shared/use-text-metrics";
import { useHoverGesture } from "@/lib/hooks/use-hover-gesture";

const DEFAULT_ROOT_IDS: [string, string] = ["prod", "eng"];

const NODE_PAD_X = 6;
const NODE_PAD_Y = 5;
const NODE_LINE_H = 13;
const NODE_CHAR_W = 5.4;
const NODE_FONT_SIZE = 11;
const NODE_MAX_LINES = 5;
const WEIGHT_GUTTER = 18;

const ROOT_BOX = { minW: 96, maxW: 150, minH: 28 };
const CHILD_BOX = { minW: 84, maxW: 132, minH: 22 };
const NODE_MAX_H = NODE_MAX_LINES * NODE_LINE_H + NODE_PAD_Y * 2;

type NodeBounds = { minW: number; maxW: number; minH: number };

interface NodeSpec {
  label: string;
  /** Width of the whole box, weight gutter included. */
  w: number;
  /** Width the label wraps in: the box minus its padding and the gutter. */
  textW: number;
  root: boolean;
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

/** Width follows the label estimate; the height comes from measuring it. */
function nodeWidth(label: string, bounds: NodeBounds, gutter: number): number {
  const ink = Math.ceil(label.length * NODE_CHAR_W);
  return clamp(ink + NODE_PAD_X * 2 + gutter, bounds.minW, bounds.maxW);
}

/**
 * First paint only, until the hidden measuring layer reports the rendered
 * height. It breaks on word boundaries like the browser does, because dividing
 * total ink by the line width undercounts every label whose last word wraps.
 */
function estimateTextHeight(item: TextMetricsItem): number {
  const textW = item.wrapWidth ?? 1;
  const words = item.text.split(/\s+/).filter(Boolean);
  let lines = 1;
  let cursor = 0;
  words.forEach((word) => {
    const ink = word.length * NODE_CHAR_W;
    const next = cursor === 0 ? ink : cursor + NODE_CHAR_W + ink;
    if (cursor > 0 && next > textW) {
      lines += 1;
      cursor = ink;
    } else {
      cursor = next;
    }
  });
  return Math.min(lines, NODE_MAX_LINES) * NODE_LINE_H;
}

function nodeHeight(textHeight: number, bounds: NodeBounds): number {
  return clamp(Math.ceil(textHeight) + NODE_PAD_Y * 2, bounds.minH, NODE_MAX_H);
}

function labelTextStyle(root: boolean): CSSProperties {
  return {
    fontSize: `${NODE_FONT_SIZE}px`,
    fontWeight: root ? 600 : 500,
    lineHeight: `${NODE_LINE_H}px`,
    textAlign: "center",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };
}

export default function ModuleParallelCategoryTrees({
  data,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleParallelCategoryTrees["data"];
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const nodes = data?.nodes ?? hierarchyDemoData;
  const rootIds = (data?.rootIds ?? DEFAULT_ROOT_IDS) as [string, string];

  const selection = useSelectionGroup();
  const node = useHoverSelect<string>({ group: selection });
  const { hovered, selected, effective } = node;
  const legend = useLegendHoverSelect({ group: selection });
  const hoverGesture = useHoverGesture();
  const treeScrollRef = useRef<HTMLDivElement>(null);

  const { nodeById, childrenOf } = useHierarchy(nodes);

  const W = 640,
    H = 172;
  const halfW = W / 2;
  const rootY = 12;
  const gapAfterRoot = 12;
  const gapChildren = 6;

  const specs = useMemo(() => {
    const out: Record<string, NodeSpec> = {};
    rootIds.forEach((rootId) => {
      const rootLabel = stripInlineMarkdown(nodeById[rootId]?.label) || rootId;
      const rootW = nodeWidth(rootLabel, ROOT_BOX, 0);
      out[rootId] = {
        label: rootLabel,
        w: rootW,
        textW: Math.max(1, rootW - NODE_PAD_X * 2),
        root: true,
      };
      (childrenOf[rootId] || []).forEach((childId) => {
        const child = nodeById[childId];
        const label = stripInlineMarkdown(child?.label) || childId;
        const gutter = child?.weight === undefined ? 0 : WEIGHT_GUTTER;
        const w = nodeWidth(label, CHILD_BOX, gutter);
        out[childId] = {
          label,
          w,
          textW: Math.max(1, w - NODE_PAD_X * 2 - gutter),
          root: false,
        };
      });
    });
    return out;
  }, [rootIds, childrenOf, nodeById]);

  const measureItems = useMemo<TextMetricsItem[]>(
    () =>
      Object.entries(specs).map(([id, spec]) => ({
        id,
        text: spec.label,
        style: labelTextStyle(spec.root),
        wrapWidth: spec.textW,
      })),
    [specs],
  );

  const { layer: labelMeasureLayer, sizes: textHeights } = useTextMetrics({
    axis: "height",
    items: measureItems,
    fallback: estimateTextHeight,
  });

  const positions = useMemo(() => {
    const pos: Record<string, { x: number; y: number; w: number; h: number; spineX?: number }> = {};
    rootIds.forEach((rootId, treeIdx) => {
      const halfLeft = treeIdx * halfW;
      const rootSpec = specs[rootId];
      if (!rootSpec) return;
      const rootH = nodeHeight(textHeights[rootId], ROOT_BOX);
      const rootX = halfLeft + (halfW - rootSpec.w) / 2 - 10;
      pos[rootId] = { x: rootX, y: rootY, w: rootSpec.w, h: rootH };
      const children = childrenOf[rootId] || [];
      const spineX = rootX + 18;
      const childX = spineX + 28;
      let y = rootY + rootH + gapAfterRoot;
      children.forEach((childId) => {
        const spec = specs[childId];
        if (!spec) return;
        const h = nodeHeight(textHeights[childId], CHILD_BOX);
        pos[childId] = { x: childX, y, w: spec.w, h, spineX };
        y += h + gapChildren;
      });
    });
    return pos;
  }, [rootIds, childrenOf, specs, textHeights, halfW]);

  const contentBottom = Object.values(positions).reduce(
    (m, pos) => Math.max(m, pos.y + pos.h),
    rootY,
  );

  const { svgRef, height: dynamicH } = useSvgContentHeight(H, [
    nodes,
    rootIds,
    contentBottom,
  ]);

  const treeOf = useCallback(
    (id: string | null): string | null => {
      if (!id) return null;
      if (rootIds.includes(id)) return id;
      const parent = nodeById[id]?.parent;
      if (parent && rootIds.includes(parent)) return parent;
      return null;
    },
    [rootIds, nodeById],
  );

  const hoveredTree = useMemo<string | null>(
    () => treeOf(effective),
    [treeOf, effective],
  );

  /**
   * Dimming a whole tree is a filter, and only the legend may filter. A node
   * hover dims while the pointer is on it; a node selection highlights the node
   * and leaves the other tree at full strength.
   */
  const transientTree = useMemo<string | null>(
    () => treeOf(hovered),
    [treeOf, hovered],
  );

  const legendItems = useMemo(
    () =>
      rootIds.map((rootId) => ({
        id: rootId,
        label: stripInlineMarkdown(nodeById[rootId]?.label) || rootId,
        value: (childrenOf[rootId] || []).length,
        color: "var(--chart-1)",
      })),
    [rootIds, nodeById, childrenOf],
  );
  const legFilter =
    legend.effective !== null ? legendItems[legend.effective]?.id ?? null : null;
  const legendSelectedIndex =
    legend.selected ?? legendIndexOfCategory(legendItems, treeOf(selected));

  // Both surfaces feed one scroll target: a legend row centres its tree, a node
  // picked inside a tree only brings that tree in when it sits outside the
  // viewport. Keying the effect on the resolved root id keeps it from firing on
  // unrelated renders, which is what makes re-picking the same row scroll again.
  const scrollRootId =
    legend.selected !== null ? rootIds[legend.selected] ?? null : treeOf(selected);
  const scrollAlign = legend.selected !== null ? "center" : "nearest";
  useEffect(() => {
    if (scrollRootId === null) return;
    const container = treeScrollRef.current;
    if (!container) return;
    scrollIntoViewX(
      container,
      container.querySelector(`[data-root-id="${scrollRootId}"]`),
      { align: scrollAlign, once: false },
    );
  }, [scrollRootId, scrollAlign]);

  return (
    <div
      className={cn("relative flex w-full flex-col", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      {labelMeasureLayer}

      <SwipeScroll ref={treeScrollRef} className="w-full">
        <div className="min-w-[680px] py-1">
          <div className="relative">
            <svg ref={svgRef} viewBox={`0 0 ${W} ${dynamicH}`} className="w-full h-auto" style={{ overflow: "visible" }}>
              <line
                x1={halfW}
                y1={rootY - 4}
                x2={halfW}
                y2={contentBottom + 4}
                stroke="var(--grid)"
                strokeDasharray="2 6"
                strokeWidth="1"
                opacity="0.4"
              />

              {rootIds.map((rootId) => {
                const rootPos = positions[rootId];
                const children = childrenOf[rootId] || [];
                if (!rootPos || children.length === 0) return null;

                const firstChildPos = positions[children[0]];
                const lastChildPos = positions[children[children.length - 1]];
                const spineX = firstChildPos.spineX!;
                const spineTop = rootPos.y + rootPos.h + 4;
                const spineBottom = lastChildPos.y + lastChildPos.h / 2;

                const isThisTree = hoveredTree === rootId;
                const otherTreeHovered =
                  transientTree !== null && transientTree !== rootId;
                const legDim = legFilter !== null && legFilter !== rootId;
                const treeDim = otherTreeHovered || legDim;

                return (
                  <g
                    key={rootId}
                    opacity={treeDim ? 0.28 : 1}
                    style={{ transition: "opacity 0.15s" }}
                  >
                    <line
                      x1={spineX}
                      y1={spineTop}
                      x2={spineX}
                      y2={spineBottom}
                      stroke={isThisTree ? "var(--chart-1)" : "var(--chart-3)"}
                      strokeWidth={isThisTree ? 1.5 : 1}
                      strokeOpacity={isThisTree ? 0.95 : 0.7}
                      strokeLinecap="round"
                      style={{ transition: "all 0.15s" }}
                    />

                    {children.map((cid) => {
                      const cp = positions[cid];
                      const cy = cp.y + cp.h / 2;
                      const specificChild = effective === cid;
                      return (
                        <line
                          key={cid}
                          x1={spineX}
                          y1={cy}
                          x2={cp.x}
                          y2={cy}
                          stroke={specificChild || isThisTree ? "var(--chart-1)" : "var(--chart-3)"}
                          strokeWidth={specificChild ? 1.75 : isThisTree ? 1.5 : 1}
                          strokeOpacity={specificChild || isThisTree ? 0.95 : 0.7}
                          strokeLinecap="round"
                          style={{ transition: "all 0.15s" }}
                        />
                      );
                    })}

                    <g
                      data-root-id={rootId}
                      onPointerEnter={(e) => {
                        if (hoverGesture.enter(e)) node.setHovered(rootId);
                      }}
                      onPointerLeave={(e) => {
                        if (hoverGesture.leave(e)) node.setHovered(null);
                      }}
                      onClick={(e) =>
                        node.select(rootId, {
                          kind: "node",
                          id: rootId,
                          element: e.currentTarget,
                        })
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <rect
                        x={rootPos.x}
                        y={rootPos.y}
                        width={rootPos.w}
                        height={rootPos.h}
                        rx="8"
                        fill="var(--card)"
                        stroke="var(--chart-1)"
                        strokeWidth={isThisTree ? 1.5 : 1.2}
                      />
                      <foreignObject
                        x={rootPos.x}
                        y={rootPos.y}
                        width={rootPos.w}
                        height={rootPos.h}
                        overflow="visible"
                      >
                        <div
                          xmlns="http://www.w3.org/1999/xhtml"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                            width: "100%",
                            height: "100%",
                            padding: `${NODE_PAD_Y}px ${NODE_PAD_X}px`,
                            color: "var(--fg)",
                          }}
                        >
                          <div style={{ ...labelTextStyle(true), width: "100%" }}>
                            {specs[rootId]?.label ?? rootId}
                          </div>
                        </div>
                      </foreignObject>
                    </g>

                    {children.map((cid) => {
                      const cp = positions[cid];
                      const child = nodeById[cid];
                      const specificChild = effective === cid;
                      const dimInTree =
                        isThisTree && !specificChild && effective !== rootId;

                      return (
                        <g
                          key={cid}
                          onPointerEnter={(e) => {
                            if (hoverGesture.enter(e)) node.setHovered(cid);
                          }}
                          onPointerLeave={(e) => {
                            if (hoverGesture.leave(e)) node.setHovered(null);
                          }}
                          onClick={(e) =>
                            node.select(cid, {
                              kind: "node",
                              id: cid,
                              group: rootId,
                              element: e.currentTarget,
                            })
                          }
                          style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                          opacity={dimInTree ? 0.5 : 1}
                        >
                          {specificChild && (
                            <rect
                              x={cp.x - 2}
                              y={cp.y - 2}
                              width={cp.w + 4}
                              height={cp.h + 4}
                              rx="8"
                              fill="none"
                              stroke="var(--chart-2)"
                              strokeWidth="1"
                              opacity="0.6"
                            />
                          )}
                          <rect
                            x={cp.x}
                            y={cp.y}
                            width={cp.w}
                            height={cp.h}
                            rx="6"
                            fill="var(--card)"
                            stroke={specificChild ? "var(--chart-2)" : "var(--chart-3)"}
                            strokeWidth={specificChild ? 1.3 : 1}
                          />
                          <foreignObject
                            x={cp.x}
                            y={cp.y}
                            width={
                              child.weight === undefined
                                ? cp.w
                                : cp.w - WEIGHT_GUTTER
                            }
                            height={cp.h}
                            overflow="visible"
                          >
                            <div
                              xmlns="http://www.w3.org/1999/xhtml"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                boxSizing: "border-box",
                                width: "100%",
                                height: "100%",
                                padding: `${NODE_PAD_Y}px ${NODE_PAD_X}px`,
                                color: "var(--fg)",
                              }}
                            >
                              <div
                                style={{ ...labelTextStyle(false), width: "100%" }}
                              >
                                {specs[cid]?.label ?? stripInlineMarkdown(child.label)}
                              </div>
                            </div>
                          </foreignObject>
                          {child.weight !== undefined && (
                            <text
                              x={cp.x + cp.w - 8}
                              y={cp.y + cp.h / 2 + 4}
                              textAnchor="end"
                              fontSize="9.5"
                              fill="var(--subtle)"
                            >
                              {child.weight}
                            </text>
                          )}
                        </g>
                      );
                    })}
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      </SwipeScroll>

      <SwipeScroll className="mt-3 pt-3 border-t border-dashed border-[var(--border-dashed)]">
        <Legend
          items={legendItems}
          hoveredIndex={legend.hovered}
          onHoverChange={legend.setHovered}
          selectedIndex={legendSelectedIndex}
          onSelect={legend.select}
          onLabelRef={legend.setLabelRef}
          className="flex flex-row gap-1"
        >
          <LegendItem className="flex shrink-0 items-center gap-2 px-2.5 py-1.5">
            <LegendMarker />
            <LegendLabel />
            <LegendValue />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </div>
  );
}
